import {
  Card,
  CardContent,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { useMemo } from "react";
import { useProjectPermissions } from "@/app/capabilities/api/hooks";
import { PROJECT } from "@/app/capabilities/permissions";
import { DataTable } from "@/components/custom/table/data-table";
import { DataTableContent } from "@/components/custom/table/data-table-content";
import { DataTablePagination } from "@/components/custom/table/data-table-pagination";
import { useColumnVisibility } from "@/components/custom/table/hooks/use-column-visibility";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { useGetProjectMembers } from "../../api/hooks";
import { getProjectMembersColumns } from "./project-members-columns";
import { ProjectMembersToolbar } from "./project-members-toolbar";

type ProjectMembersDashboardProps = {
  showCountSummary?: boolean;
};

export function ProjectMembersDashboard({
  showCountSummary = true,
}: ProjectMembersDashboardProps) {
  const { organizationId, projectId } = useOrgProjectRouteContext();
  const { data } = useGetProjectMembers();

  const { canProject } = useProjectPermissions();
  const canDelete = canProject(PROJECT.members.delete);

  // Role data is only present when operator-RBAC is enabled; without it the
  // column has nothing to show, so omit it entirely (legacy/flag-off).
  const showRole = (data?.items ?? []).some((member) => Boolean(member.role));
  const columns = useMemo(
    () => getProjectMembersColumns(showRole, canDelete),
    [showRole, canDelete]
  );

  const [columnVisibility, setColumnVisibility] = useColumnVisibility(
    columns,
    `project-members-columns-${organizationId}-${projectId}`
  );

  const members = data?.items ?? [];
  const totalMembers = data?.count ?? members.length;

  return members.length === 0 ? (
    <Card className="border-dashed">
      <CardContent className="py-8 text-muted-foreground text-sm">
        <div className="mb-1 font-medium text-foreground">No members found</div>
        <div>Invite teammates to start collaborating.</div>
      </CardContent>
    </Card>
  ) : (
    <div className="space-y-3">
      {!!showCountSummary && (
        <div className="text-muted-foreground text-sm">
          <span className="font-semibold text-foreground tabular-nums">
            {totalMembers}
          </span>{" "}
          members
        </div>
      )}
      <DataTable
        className="space-y-4"
        columns={columns}
        columnVisibility={columnVisibility}
        data={members}
        defaultPageSize={10}
        getRowId={(row) => row.account_id}
        onColumnVisibilityChange={setColumnVisibility}
      >
        <ProjectMembersToolbar />
        <DataTableContent stickyColumnBg="card" />
        <DataTablePagination />
      </DataTable>
    </div>
  );
}
